import React, { useState } from 'react';
import { Box, Button, CircularProgress, Link, TextField, Typography } from '@mui/material';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuthShell from '../components/auth/AuthShell';
import PasswordField from '../components/auth/PasswordField';
import { errorBoxSx, primaryButtonSx, underlineStyle } from '../components/auth/authStyles';
import { useAuth } from '../contexts/AuthContext';
import { fieldErrors } from '../services/api';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function RegisterPage() {
    const { t } = useTranslation();
    const { register } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({ companyName: '', firstName: '', lastName: '', email: '', password: '', confirmPassword: '' });
    const [errors, setErrors] = useState({});
    const [serverError, setServerError] = useState(null);
    const [loading, setLoading] = useState(false);

    const onChange = (e) => {
        const { name, value } = e.target;
        setForm(f => ({ ...f, [name]: value }));
        if (errors[name]) setErrors(x => ({ ...x, [name]: undefined }));
        if (serverError) setServerError(null);
    };

    const validate = () => {
        const v = {};
        if (!form.companyName.trim()) v.companyName = t('auth.register.errors.companyRequired');
        if (!form.firstName.trim()) v.firstName = t('validation.required');
        if (!form.lastName.trim()) v.lastName = t('validation.required');
        if (!form.email.trim()) v.email = t('validation.required');
        else if (!EMAIL_RE.test(form.email.trim())) v.email = t('validation.email');
        if (form.password.length < 8) v.password = t('auth.register.errors.passwordShort');
        if (form.confirmPassword !== form.password) v.confirmPassword = t('auth.register.errors.passwordMismatch');
        setErrors(v);
        return Object.keys(v).length === 0;
    };

    const submit = async (e) => {
        e?.preventDefault();
        if (!validate()) return;
        setLoading(true);
        try {
            await register({
                companyName: form.companyName.trim(),
                firstName: form.firstName.trim(),
                lastName: form.lastName.trim(),
                email: form.email.trim(),
                password: form.password,
            });
            navigate('/dashboard', { replace: true });
        } catch (err) {
            const code = err?.response?.data?.code;
            if (code === 'EMAIL_TAKEN') setErrors(x => ({ ...x, email: t('auth.register.errors.emailTaken') }));
            else if (code === 'VALIDATION_FAILED') setErrors(fieldErrors(err));
            else setServerError(t('auth.register.errors.generic'));
        } finally {
            setLoading(false);
        }
    };

    const field = (name, label, extra = {}) => (
        <TextField
            fullWidth variant="standard" name={name} label={label}
            value={form[name]} onChange={onChange}
            error={!!errors[name]} helperText={errors[name] || ' '}
            sx={underlineStyle} {...extra}
        />
    );

    return (
        <AuthShell formMaxWidth={420}>
            <Box component="form" onSubmit={submit} noValidate>
                <Typography variant="h5" fontWeight={700} sx={{ color: '#1a2332', mb: 0.5 }}>{t('auth.register.title')}</Typography>
                <Typography variant="body2" sx={{ color: '#7f8c9a', mb: 3 }}>{t('auth.register.subtitle')}</Typography>

                {serverError && <Box sx={errorBoxSx}><Typography variant="body2" color="error">{serverError}</Typography></Box>}

                {/* Company block: the firm is the account (tenant), so it comes first and is required. */}
                <Box sx={{ p: 2, pb: 0.5, mb: 2, borderRadius: '14px', bgcolor: 'rgba(66,120,152,0.06)', border: '1px solid rgba(66,120,152,0.14)' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <ApartmentOutlinedIcon sx={{ fontSize: 18, color: '#427898' }} />
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#427898', textTransform: 'uppercase', letterSpacing: 0.8 }}>
                            {t('auth.register.companySection')}
                        </Typography>
                    </Box>
                    {field('companyName', t('auth.fields.companyName'), { autoFocus: true, required: true, autoComplete: 'organization' })}
                </Box>

                <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.8, mb: 0.5 }}>
                    {t('auth.register.adminSection')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    {field('firstName', t('auth.fields.firstName'), { autoComplete: 'given-name' })}
                    {field('lastName', t('auth.fields.lastName'), { autoComplete: 'family-name' })}
                </Box>
                {field('email', t('auth.fields.email'), { type: 'email', autoComplete: 'email' })}
                <PasswordField
                    name="password" label={t('auth.fields.password')} autoComplete="new-password"
                    value={form.password} onChange={onChange}
                    error={!!errors.password} helperText={errors.password || t('auth.register.passwordHint')}
                />
                <PasswordField
                    name="confirmPassword" label={t('auth.fields.confirmPassword')} autoComplete="new-password"
                    value={form.confirmPassword} onChange={onChange}
                    error={!!errors.confirmPassword} helperText={errors.confirmPassword || ' '}
                    sx={{ mt: 1 }}
                />

                <Button type="submit" fullWidth variant="contained" disabled={loading} sx={{ ...primaryButtonSx, mt: 2 }}>
                    {loading ? <CircularProgress size={22} sx={{ color: 'white' }} /> : t('auth.register.submit')}
                </Button>
                <Typography variant="caption" component="p" sx={{ textAlign: 'center', mt: 1.5, color: '#94A3B8' }}>
                    {t('auth.register.freeNote')}
                </Typography>
                <Typography variant="body2" sx={{ textAlign: 'center', mt: 2, color: '#7f8c9a' }}>
                    {t('auth.register.haveAccount')}{' '}
                    <Link component={RouterLink} to="/login" sx={{ color: '#427898', fontWeight: 600 }}>{t('auth.register.loginLink')}</Link>
                </Typography>
            </Box>
        </AuthShell>
    );
}
